import { createStreamLainCompositorController } from './streamlain-compositor-controller';
import {
  STREAMLAIN_MOTION,
  type StreamLainAnimationHandle,
  type StreamLainMotionKind,
  type StreamLainMotionOutcome,
} from './streamlain-motion-curves';
import {
  isStreamLainMotionEnabled,
  type StreamLainMotionDomain,
  subscribeStreamLainMotionPolicy,
} from './streamlain-motion-policy';
import {
  type StreamLainMotionChannel,
  streamLainMotionChannel,
  type StreamLainMotionScheduler,
  streamLainMotionScheduler,
} from './streamlain-motion-scheduler';

export * from './streamlain-motion-curves';
export * from './streamlain-motion-policy';

/** One in-flight animation per element. Cancellation never rejects its outcome. */
/** Layer motion of a side panel's content: slides in from its edge, fades out. */
export const STREAMLAIN_PANEL_LAYER_ENTER: Keyframe[] = [
  {
    opacity: 0,
    transform: `translateX(${STREAMLAIN_MOTION.overlay.offset}px)`,
  },
  { opacity: 1, transform: 'translateX(0)' },
];
export const STREAMLAIN_PANEL_LAYER_EXIT: Keyframe[] = [
  { opacity: 1 },
  { opacity: 0 },
];

export class StreamLainAnimationController {
  private readonly compositor = createStreamLainCompositorController({
    isEnabled: isStreamLainMotionEnabled,
    subscribe: subscribeStreamLainMotionPolicy,
  });
  private readonly active = new WeakMap<Element, StreamLainAnimationHandle>();
  private readonly channels = new WeakMap<Element, StreamLainMotionChannel>();
  private readonly regions = new WeakMap<
    HTMLElement,
    {
      revision: symbol;
      overflow: string;
    }
  >();

  constructor(
    private readonly scheduler: StreamLainMotionScheduler = streamLainMotionScheduler
  ) {}

  cancel(element: Element): void {
    this.active.get(element)?.cancel();
  }

  /** Apply an external/native state change without a stale reveal completing later. */
  settleRegion(element: HTMLElement, expanded: boolean): void {
    const region = this.regions.get(element);
    this.cancel(element);
    this.regions.delete(element);
    if (region) element.style.overflow = region.overflow;
    element.hidden = !expanded;
    element.inert = !expanded;
  }

  /** Animate a content resize from its previous natural height, including interrupted motion. */
  resizeRegion(
    element: HTMLElement,
    previousHeight: number,
    nextHeight: number
  ): StreamLainAnimationHandle {
    return this.reveal(element, true, { previousHeight, nextHeight });
  }

  /**
   * Animate an element's inline size after a layout change by capping its
   * `max-width` per frame, from the width measured before the change (or the
   * value of a running resize) to the new layout width. The element keeps its
   * own alignment, so a centred element grows and shrinks symmetrically, and
   * the cap is removed once the motion settles.
   */
  resizeInline(
    element: HTMLElement,
    previousWidth: number
  ): StreamLainAnimationHandle {
    const channel = this.channelFor(element);
    const from = this.scheduler.value(channel) ?? previousWidth;
    this.cancel(element);
    element.style.maxWidth = '';
    const to = element.getBoundingClientRect().width;
    if (Math.abs(to - from) < 1) {
      return { finished: Promise.resolve('finished'), cancel() {} };
    }
    element.style.maxWidth = `${from}px`;
    return this.run(element, channel, settle =>
      this.scheduler.animate({
        channel,
        kind: 'resize',
        from,
        to,
        onUpdate: value => {
          element.style.maxWidth = `${value}px`;
        },
        onComplete: () => {
          element.style.maxWidth = '';
          settle('finished');
        },
        onCancel: () => settle('cancelled'),
      })
    );
  }

  /**
   * Step a layout value owned by the element's renderer (e.g. a column width
   * that drives several positioned children) along the shared motion curve.
   * `onUpdate` receives the progress from 0 to 1; the scheduler hands it the
   * end state at once under reduced motion.
   */
  animateProgress(
    element: Element,
    onUpdate: (progress: number) => void,
    kind: StreamLainMotionKind = 'resize',
    domain: StreamLainMotionDomain = 'app'
  ): StreamLainAnimationHandle {
    const channel = this.channelFor(element);
    this.cancel(element);
    return this.run(element, channel, settle =>
      this.scheduler.animate({
        channel,
        kind,
        domain,
        from: 0,
        to: 1,
        onUpdate,
        onComplete: () => settle('finished'),
        onCancel: () => settle('cancelled'),
      })
    );
  }

  /**
   * Scroll a container along the shared motion curve. Scroll offsets are not
   * animatable through WAAPI, so the motion scheduler steps them per frame;
   * the outcome and cancellation semantics match every other animation.
   */
  scrollTo(
    element: HTMLElement,
    target: { left?: number; top?: number },
    kind: StreamLainMotionKind = 'resize'
  ): StreamLainAnimationHandle {
    const channel = this.channelFor(element);
    this.cancel(element);
    const fromLeft = element.scrollLeft;
    const fromTop = element.scrollTop;
    const toLeft = target.left ?? fromLeft;
    const toTop = target.top ?? fromTop;
    if (
      this.scheduler.prefersReducedMotion() ||
      (Math.abs(toLeft - fromLeft) < 1 && Math.abs(toTop - fromTop) < 1)
    ) {
      element.scrollTo({ left: toLeft, top: toTop, behavior: 'auto' });
      return { finished: Promise.resolve('finished'), cancel() {} };
    }
    return this.run(element, channel, settle =>
      this.scheduler.animate({
        channel,
        kind,
        from: 0,
        to: 1,
        onUpdate: progress => {
          element.scrollTo({
            left: fromLeft + (toLeft - fromLeft) * progress,
            top: fromTop + (toTop - fromTop) * progress,
            behavior: 'auto',
          });
        },
        onComplete: () => settle('finished'),
        onCancel: () => settle('cancelled'),
      })
    );
  }

  reveal(
    element: HTMLElement,
    expanded: boolean,
    resize?: { previousHeight: number; nextHeight: number }
  ): StreamLainAnimationHandle {
    const view = element.ownerDocument.defaultView;
    if (!view) {
      throw new Error('Motion requires a document with a browsing context');
    }
    const revision = Symbol('reveal');
    const previous = this.regions.get(element);
    const overflow = previous?.overflow ?? element.style.overflow;
    const currentStyle = view.getComputedStyle(element);
    const height = element.hidden
      ? '0px'
      : resize && !this.active.has(element)
        ? `${resize.previousHeight}px`
        : currentStyle.height;
    const opacity = element.hidden ? 0 : Number(currentStyle.opacity);
    this.regions.set(element, { revision, overflow });
    this.cancel(element);
    element.hidden = false;
    element.inert = !expanded;
    element.style.overflow = 'hidden';
    const targetHeight = expanded
      ? resize
        ? `${resize.nextHeight}px`
        : view.getComputedStyle(element).height
      : '0px';
    const handle = this.play(
      element,
      [
        { height, opacity },
        { height: targetHeight, opacity: expanded ? 1 : 0 },
      ],
      'resize'
    );
    return {
      cancel: handle.cancel,
      finished: handle.finished.then(outcome => {
        if (this.regions.get(element)?.revision === revision) {
          element.hidden = !expanded;
          element.style.overflow = overflow;
          this.regions.delete(element);
        }
        return outcome;
      }),
    };
  }

  play(
    element: Element,
    keyframes: Keyframe[],
    kind: StreamLainMotionKind,
    domain: StreamLainMotionDomain = 'app'
  ): StreamLainAnimationHandle {
    this.cancel(element);
    return this.trackCompositor(
      element,
      this.compositor.play(
        element,
        keyframes,
        { ...STREAMLAIN_MOTION[kind], fill: 'both' },
        domain
      )
    );
  }

  /** Native repeating frames; no scheduler/rAF work during heavy document loads. */
  playLoop(
    element: Element,
    keyframes: Keyframe[],
    durationMs: number,
    startTime?: number | null
  ): StreamLainAnimationHandle {
    this.cancel(element);
    return this.trackCompositor(
      element,
      this.compositor.playLoop(element, keyframes, durationMs, startTime)
    );
  }

  isEnabled(element: Element): boolean {
    return this.compositor.isEnabled(element);
  }

  private trackCompositor(
    element: Element,
    handle: StreamLainAnimationHandle
  ): StreamLainAnimationHandle {
    this.active.set(element, handle);
    void handle.finished.then(() => {
      if (this.active.get(element) === handle) this.active.delete(element);
    });
    return handle;
  }

  private channelFor(element: Element): StreamLainMotionChannel {
    let channel = this.channels.get(element);
    if (!channel) {
      channel = streamLainMotionChannel('element-motion');
      this.channels.set(element, channel);
    }
    return channel;
  }

  /** Register a scheduler-driven motion as the element's single active animation. */
  private run(
    element: Element,
    channel: StreamLainMotionChannel,
    start: (settle: (outcome: StreamLainMotionOutcome) => void) => void
  ): StreamLainAnimationHandle {
    let settle!: (outcome: StreamLainMotionOutcome) => void;
    const finished = new Promise<StreamLainMotionOutcome>(resolve => {
      settle = resolve;
    });
    let settled = false;
    const handle: StreamLainAnimationHandle = {
      finished,
      cancel: () => this.scheduler.cancel(channel),
    };
    const complete = (outcome: StreamLainMotionOutcome) => {
      if (settled) return;
      settled = true;
      if (this.active.get(element) === handle) this.active.delete(element);
      settle(outcome);
    };
    this.active.set(element, handle);
    start(complete);
    return handle;
  }
}

export const streamLainAnimationController =
  new StreamLainAnimationController();
